import { useEffect, useState } from 'preact/hooks';
import { AppLayout } from '../../components/app/AppLayout';
import { RequireOnboarded } from '../../components/app/RequireOnboarded';
import { useI18n } from '../../i18n';
import { useGameData } from '../../state/game';
import { BottleCap } from '../../components/app/BottleCap';
import { StatusBadge } from '../../components/app/StatusBadge';
import { ScoreStepper } from '../../components/app/ScoreStepper';
import { CrowdPicksBar, outcomeOf } from '../../components/app/CrowdPicksBar';
import { ChatPanel } from '../../components/match/ChatPanel';
import { Icon } from '../../components/app/icons';
import { useApiAuth } from '../../lib/useApi';
import { matchChatSource } from '../../lib/comments';
import { fetchDistribution, type Distribution } from '../../lib/gameData';
import {
  fetchMatchDetail,
  fetchTvLinks,
  type MatchDetailView,
  type MatchEvent,
  type MatchStat,
  type TvLink,
} from '../../lib/matchDetail';
import { shortDate, shortTime } from '../../lib/datetime';
import { isLocked, sideLabel, gradeScore, wentToExtraTime, wentToPens } from '../../lib/wcTypes';
import type { ApiError } from '../../lib/http';

export function MatchDetail({ n }: { n: string }) {
  return (
    <RequireOnboarded>
      <AppLayout>
        <MatchDetailInner num={parseInt(n, 10)} />
      </AppLayout>
    </RequireOnboarded>
  );
}

function MatchDetailInner({ num }: { num: number }) {
  const { t, locale, localePath } = useI18n();
  const auth = useApiAuth();
  const { bundle, loading, rules, pickFor, pointsFor, boostsUsed, submitPick } = useGameData();

  const match = bundle?.matches.find((m) => m.number === num);
  const existing = pickFor(num);

  const [scoreA, setScoreA] = useState(existing?.scoreA ?? 0);
  const [scoreB, setScoreB] = useState(existing?.scoreB ?? 0);
  const [boost, setBoost] = useState(existing?.boost ?? false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [dist, setDist] = useState<Distribution | null>(null);
  const [detail, setDetail] = useState<MatchDetailView | null>(null);
  const [tvLinks, setTvLinks] = useState<TvLink[]>([]);
  const [tab, setTab] = useState<MdTab>('timeline');
  const [tabTouched, setTabTouched] = useState(false);

  // Sync local form when the cached pick resolves (predictions load async).
  useEffect(() => {
    if (existing) {
      setScoreA(existing.scoreA);
      setScoreB(existing.scoreB);
      setBoost(existing.boost);
    }
  }, [existing?.scoreA, existing?.scoreB, existing?.boost]);

  useEffect(() => {
    fetchDistribution(num).then(setDist).catch(() => setDist(null));
  }, [num]);

  // Enhanced detail (events/stats) + broadcast links. Refresh while live.
  useEffect(() => {
    let alive = true;
    fetchMatchDetail(num).then((d) => alive && setDetail(d)).catch(() => {});
    fetchTvLinks(num).then((l) => alive && setTvLinks(l)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [num]);

  const live = match?.status === 'live';
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => {
      fetchMatchDetail(num).then(setDetail).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [live, num]);

  // Default tab follows match state (timeline once there's action, chat
  // before kickoff) until the user taps a tab themselves.
  useEffect(() => {
    if (tabTouched || !match) return;
    setTab(match.status === 'live' || match.status === 'done' ? 'timeline' : 'chat');
  }, [match?.status, tabTouched]);

  if (loading || !bundle) return <div class="bc-spinner">{t('app.loading')}</div>;
  if (!match) {
    return (
      <div class="bc-empty">
        {t('game.not_found')}
        <div style={{ marginTop: 16 }}>
          <a class="bc-pill" href={localePath('/play')}>{t('game.back_to_play')}</a>
        </div>
      </div>
    );
  }

  const locked = isLocked(match);
  const teamA = match.team_a ? bundle.teams[match.team_a] : undefined;
  const teamB = match.team_b ? bundle.teams[match.team_b] : undefined;
  const nameA = sideLabel(bundle, match.team_a, match.team_a_slot, locale);
  const nameB = sideLabel(bundle, match.team_b, match.team_b_slot, locale);
  const stadium = bundle.stadiums[match.stadium];
  // Primary score = 90' full-time (what's graded); the actual a.e.t./pens
  // result is shown beneath when it differs.
  const grade = gradeScore(match);
  const koExtras: string[] = [];
  if (wentToExtraTime(match)) koExtras.push(`${t('game.aet')} ${match.score_a}–${match.score_b}`);
  if (wentToPens(match)) koExtras.push(`${match.score_a_pens}–${match.score_b_pens} ${t('game.pens')}`);
  // KO slots not yet resolved → can't predict.
  const predictable = !locked && !!match.team_a && !!match.team_b;

  // A boost is available if we're under the cap, accounting for this match's
  // own currently-saved boost (so re-confirming an already-boosted pick is ok).
  const ownBoost = existing?.boost ? 1 : 0;
  const boostAtCap = boostsUsed - ownBoost >= rules.boostTokens;

  async function confirm() {
    if (!predictable || saving) return;
    setSaving(true);
    setMsg(null);
    try {
      await submitPick(num, { scoreA, scoreB, boost });
      setMsg({ kind: 'ok', text: t('game.saved') });
      fetchDistribution(num).then(setDist).catch(() => {});
    } catch (e) {
      const code = (e as ApiError).code;
      if (code === 'match_locked') setMsg({ kind: 'err', text: t('game.match_locked') });
      else if (code === 'boost_limit') {
        setBoost(false);
        setMsg({ kind: 'err', text: t('game.boost_limit') });
      } else setMsg({ kind: 'err', text: t('game.save_failed') });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="md">
      <a class="md__back" href={localePath('/play')}>
        <Icon.back size={18} /> {t('game.back_to_play')}
      </a>

      <div class="md__hero">
        <div class="md__hero-row">
          <span class="bc-mono md__num">#{match.number}</span>
          <StatusBadge status={match.status} minute={match.minute} />
        </div>
        <div class="md__teams">
          <div class="md__team">
            <BottleCap flag={teamA?.flag} code={match.team_a} size={64} title={nameA} />
            <span class="md__name">{nameA}</span>
          </div>
          <div class="md__score">
            {grade ? (
              <>
                <span class="bc-serif">{grade[0]}–{grade[1]}</span>
                {koExtras.length > 0 ? (
                  <span class="md__score-extra">{koExtras.join('  ·  ')}</span>
                ) : null}
              </>
            ) : (
              <span class="bc-serif md__vs">{t('game.vs')}</span>
            )}
          </div>
          <div class="md__team">
            <BottleCap flag={teamB?.flag} code={match.team_b} size={64} title={nameB} />
            <span class="md__name">{nameB}</span>
          </div>
        </div>
        <div class="md__meta">
          {shortDate(match.kickoff_utc, locale)} · {shortTime(match.kickoff_utc, locale)}
          {stadium ? ` · ${stadium.name}, ${stadium.city}` : ''}
        </div>
      </div>

      <div class={`bc-card md__pred${boost ? ' is-boosted' : ''}`}>
        {predictable ? (
          <>
            <div class="bc-label">{t('game.your_prediction')}</div>
            <div class="md__steppers">
              <ScoreStepper value={scoreA} onChange={setScoreA} />
              <span class="md__dash">–</span>
              <ScoreStepper value={scoreB} onChange={setScoreB} color="var(--loss)" />
            </div>

            <button
              type="button"
              class={`md__boost${boost ? ' is-on' : ''}`}
              disabled={!boost && boostAtCap}
              onClick={() => setBoost((b) => !b)}
            >
              <Icon.boost size={18} />
              <span>{t('game.boost')} ×{rules.boostMultiplier}</span>
              <small>
                {boostAtCap && !boost
                  ? t('game.boost_none_left')
                  : `${rules.boostTokens - boostsUsed + ownBoost} ${t('game.boost_left')}`}
              </small>
            </button>

            <button class="bc-cta" disabled={saving} onClick={confirm}>
              {saving ? t('onb.saving') : boost ? t('game.confirm_boost') : t('game.confirm')}
            </button>
          </>
        ) : (
          <div class="md__locked">
            {existing ? (
              <p>
                {t('game.your_pick')} <strong>{existing.scoreA}–{existing.scoreB}</strong>
                {existing.boost ? ' ×2' : ''}
                {match.status === 'done' ? ` · +${pointsFor(num)} ${t('game.points')}` : ''}
              </p>
            ) : (
              <p>{!match.team_a || !match.team_b ? t('game.tbd') : t('game.locked_no_pick')}</p>
            )}
          </div>
        )}

        {msg && <p class={`md__msg md__msg--${msg.kind}`}>{msg.text}</p>}
      </div>

      <div class="bc-card md__crowd">
        <div class="bc-label">{t('game.crowd')}</div>
        <CrowdPicksBar
          dist={dist}
          showLabels
          myPick={existing ? outcomeOf(existing.scoreA, existing.scoreB) : undefined}
        />
      </div>

      <div class="bc-phasetabs md__tabs">
        {(['timeline', 'stats', 'chat', ...(tvLinks.length > 0 ? ['tv'] as MdTab[] : [])] as MdTab[]).map(
          (tk) => (
            <button
              key={tk}
              class={`bc-pill${tab === tk ? ' is-active' : ''}`}
              onClick={() => {
                setTab(tk);
                setTabTouched(true);
              }}
            >
              {t(`game.tab_${tk}`)}
            </button>
          ),
        )}
      </div>

      <div class="bc-card md__tabpanel">
        {tab === 'timeline' &&
          (detail && detail.events.length > 0 ? (
            <Timeline events={detail.events} homeCode={match.team_a} />
          ) : (
            <p class="bc-empty">{t('game.no_events')}</p>
          ))}

        {tab === 'stats' &&
          (detail?.stats && detail.stats.length > 0 ? (
            <StatsTable stats={detail.stats} homeCode={match.team_a} t={t} />
          ) : (
            <p class="bc-empty">{t('game.no_stats')}</p>
          ))}

        {tab === 'chat' && <ChatPanel source={matchChatSource(auth, num)} />}

        {tab === 'tv' && (
          <div class="md__tvlist">
            {tvLinks.map((l) => (
              <TvRow key={l.id} link={l} freeLabel={t('game.tv_free')} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

type MdTab = 'timeline' | 'stats' | 'chat' | 'tv';

/** A marker glyph for an event type. Cards read their colour from meta.detail. */
function eventGlyph(e: MatchEvent): string {
  const type = e.eventType.toLowerCase();
  if (type === 'goal') return '⚽';
  if (type === 'subst') return '🔄';
  if (type === 'var') return '🎥';
  if (type === 'card') {
    const detail = String(e.meta?.detail ?? e.meta?.card ?? '').toLowerCase();
    return detail.includes('red') ? '🟥' : '🟨';
  }
  return '•';
}

function Timeline({ events, homeCode }: { events: MatchEvent[]; homeCode?: string }) {
  const sorted = [...events].sort(
    (a, b) => a.minute - b.minute || (a.minuteExtra ?? 0) - (b.minuteExtra ?? 0),
  );
  return (
    <div class="tl">
      {sorted.map((e) => {
        const home = !!homeCode && e.teamCode === homeCode;
        const min = `${e.minute}${e.minuteExtra ? `+${e.minuteExtra}` : ''}'`;
        return (
          <div key={e.id} class={`tl__row${home ? ' tl__row--home' : ' tl__row--away'}`}>
            <span class="tl__min bc-mono">{min}</span>
            <span class="tl__glyph">{eventGlyph(e)}</span>
            <span class="tl__who">
              {e.playerName}
              {e.relatedPlayer ? <small> · {e.relatedPlayer}</small> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const STAT_ROWS: { key: keyof MatchStat; label: string; pct?: boolean }[] = [
  { key: 'possessionPct', label: 'stat_possession', pct: true },
  { key: 'shotsTotal', label: 'stat_shots' },
  { key: 'shotsOn', label: 'stat_shots_on' },
  { key: 'corners', label: 'stat_corners' },
  { key: 'fouls', label: 'stat_fouls' },
  { key: 'offsides', label: 'stat_offsides' },
  { key: 'yellowCards', label: 'stat_yellow' },
  { key: 'redCards', label: 'stat_red' },
  { key: 'passesPct', label: 'stat_pass_pct', pct: true },
  { key: 'saves', label: 'stat_saves' },
];

function StatsTable({
  stats,
  homeCode,
  t,
}: {
  stats: MatchStat[];
  homeCode?: string;
  t: (k: string) => string;
}) {
  const a = (homeCode && stats.find((s) => s.teamCode === homeCode)) || stats[0];
  const b = stats.find((s) => s !== a) ?? stats[1];
  if (!a || !b) return null;
  return (
    <div class="statgrid">
      {STAT_ROWS.map(({ key, label, pct }) => {
        const va = a[key] as number | undefined;
        const vb = b[key] as number | undefined;
        if (va == null && vb == null) return null;
        const fmt = (v?: number) => (v == null ? '–' : pct ? `${v}%` : String(v));
        return (
          <div key={key} class="statgrid__row">
            <span class="statgrid__a bc-mono">{fmt(va)}</span>
            <span class="statgrid__label">{t(`game.${label}`)}</span>
            <span class="statgrid__b bc-mono">{fmt(vb)}</span>
          </div>
        );
      })}
    </div>
  );
}

function TvRow({ link, freeLabel }: { link: TvLink; freeLabel: string }) {
  const meta = [link.region, link.language].filter(Boolean).join(' · ');
  const inner = (
    <>
      <span class="md__tvch">{link.channel}</span>
      {meta && <span class="md__tvmeta">{meta}</span>}
      {link.isFree && <span class="md__tvfree">{freeLabel}</span>}
    </>
  );
  return link.url ? (
    <a class="md__tvrow md__tvrow--link" href={link.url} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  ) : (
    <div class="md__tvrow">{inner}</div>
  );
}
